import { pageSlugFromMarkdown } from './itemImages';
import { parentDirOfMarkdown } from './markdownPaths';

/** Repo folder for audio belonging to `database/foo/bar.md` → `database/foo/bar/_audio/ */
export function audioDirForMarkdown(mdPath: string): string {
  return mdPath.replace(/\.md$/i, '/_audio');
}

/** `garden-notes` → `database/parent/garden-notes/_audio`, plus `-`/`_` folder variants. */
export function audioDirsForPageSlug(parentDir: string, slug: string): string[] {
  const variants = new Set<string>([slug]);
  const underscored = slug.replace(/-/g, '_');
  const hyphenated = slug.replace(/_/g, '-');
  variants.add(underscored);
  variants.add(hyphenated);
  return [...variants].map(s => `${parentDir}/${s}/_audio`);
}

export function audioRepoPath(mdPath: string, filename: string): string {
  return `${audioDirForMarkdown(mdPath)}/${filename}`;
}

/** Paths to try when loading (canonical folder, slug spelling variants, sibling legacy folders). */
export function alternateAudioRepoPaths(repoPath: string, mdPath?: string): string[] {
  const paths: string[] = [repoPath];
  const m = /^(database\/[^/]+)(\/_audio\/.+)$/i.exec(repoPath);
  if (m) {
    const dir = m[1]!;
    const rest = m[2]!;
    const underscored = dir.replace(/-/g, '_');
    const hyphenated = dir.replace(/_/g, '-');
    if (underscored !== dir) paths.push(`${underscored}${rest}`);
    if (hyphenated !== dir) paths.push(`${hyphenated}${rest}`);
  }

  if (mdPath) {
    const filename = repoPath.split('/').pop();
    if (filename) {
      const parent = parentDirOfMarkdown(mdPath);
      const slug = pageSlugFromMarkdown(mdPath);
      for (const dir of audioDirsForPageSlug(parent, slug)) {
        paths.push(`${dir}/${filename}`);
      }
    }
  }

  return [...new Set(paths)];
}

const ALLOWED_EXT = new Set(['mp3', 'm4a', 'wav', 'ogg', 'webm', 'aac', 'flac']);

function randomAudioId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function newAudioFilename(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase() ?? '';
  let ext = ALLOWED_EXT.has(fromName) ? fromName : '';
  if (!ext && file.type) {
    const m = /^audio\/(?:mpeg|mp4|wav|ogg|webm|aac|flac|x-m4a)/i.exec(file.type);
    if (m) {
      if (file.type.includes('mpeg')) ext = 'mp3';
      else if (file.type.includes('mp4') || file.type.includes('m4a')) ext = 'm4a';
      else if (file.type.includes('wav')) ext = 'wav';
      else if (file.type.includes('ogg')) ext = 'ogg';
      else if (file.type.includes('webm')) ext = 'webm';
      else if (file.type.includes('aac')) ext = 'aac';
      else if (file.type.includes('flac')) ext = 'flac';
    }
  }
  if (!ext) ext = 'mp3';
  return `${randomAudioId()}.${ext}`;
}

export function mimeForAudioPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'mp3':
      return 'audio/mpeg';
    case 'm4a':
      return 'audio/mp4';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
      return 'audio/ogg';
    case 'webm':
      return 'audio/webm';
    case 'aac':
      return 'audio/aac';
    case 'flac':
      return 'audio/flac';
    default:
      return 'application/octet-stream';
  }
}

/** Short label for UI (filename without path). */
export function audioDisplayLabel(filename: string): string {
  return filename.length > 28 ? `${filename.slice(0, 25)}…` : filename;
}
